import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { listScheduleEventsPage } from '@sportninja/common/services/event'

interface UseScheduleHasEventsArgs {
  scheduleId?: string | null
  /**
   * Skips the query when the answer cannot change the outcome — the caller
   * already knows the competition has games, so it is showing the list either
   * way and does not need to pay for this fetch.
   */
  enabled?: boolean
}

interface UseScheduleHasEventsReturn {
  /** True once the endpoint has answered with at least one event. */
  hasEvents: boolean
  /** True while the answer is still unknown. */
  isLoading: boolean
}

/**
 * Sized to the question. This asks whether ANY event exists — one row proves
 * it, and no further row can change the answer.
 */
const EXISTS_PER_PAGE = 1

/**
 * Whether a competition holds any events at all.
 *
 * The Schedule tab decides between the setup wizard and the real list from
 * `games_count`, which the schedule item carries. That field counts GAMES
 * only — there is no `events_count` on the API today — so a competition whose
 * entries are all events looked empty and was shown the wizard, with the list
 * (and therefore the events fetch) never mounting at all.
 *
 * This asks the events endpoint directly rather than waiting for a count field
 * to exist. It is deliberately a separate, narrowly-scoped query: the gate has
 * to answer before the list mounts, so it cannot read the list's own data.
 *
 * ## Why one page, not the whole list
 *
 * This used to call the fetch-all service with an empty query, which drained
 * every page at the API's default size — three to five requests, and every
 * event in the competition downloaded, to compute `length > 0`. It also carried
 * a claim that the cost was shared with the schedule list via
 * `events.bySchedule`; that was never true (the list passes a real filter
 * object, this passed `null`, so the keys never matched) and is now
 * structurally impossible, since the list is on `byScheduleStream`.
 *
 * `per_page: 1` answers the same question in one request.
 */
export const useScheduleHasEvents = ({
  scheduleId,
  enabled = true,
}: UseScheduleHasEventsArgs): UseScheduleHasEventsReturn => {
  'use memo'

  const query = useQuery({
    queryKey: queryKeys.events.exists(scheduleId ?? null),
    queryFn: () =>
      listScheduleEventsPage(
        scheduleId as string,
        { per_page: EXISTS_PER_PAGE },
        1
      ),
    enabled: enabled && Boolean(scheduleId),
  })

  return {
    hasEvents: (query.data?.data?.length ?? 0) > 0,
    isLoading: query.isLoading,
  }
}
